/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

export default function OrganizationGroupForm({ applicationId, group, parentGroup, onClose, onSuccess }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();

    const [name, setName] = useState('');
    const [groupTypeId, setGroupTypeId] = useState('');
    const [loading, setLoading] = useState(false);
    const [typesLoading, setTypesLoading] = useState(false);

    const [availableTypes, setAvailableTypes] = useState([]);
    const [appTables, setAppTables] = useState([]);
    const [defaultTables, setDefaultTables] = useState([]);
    const [tableSearch, setTableSearch] = useState('');

    // Load available types based on parent or if root
    useEffect(() => {
        fetchTypes();
        fetchTables();
    }, [applicationId, parentGroup]);

    useEffect(() => {
        if (group) {
            setName(group.name);
            const gTypeId = (group.group_type && typeof group.group_type === 'object')
                ? group.group_type.id
                : group.group_type;
            setGroupTypeId(gTypeId || '');
            setDefaultTables(group.default_tables || []);
        } else {
            setName('');
            setDefaultTables([]);
            // If creating new and we have only one available type, select it automatically
            if (availableTypes.length === 1) {
                setGroupTypeId(availableTypes[0].id);
            }
        }
    }, [group, availableTypes]);

    const fetchTables = async () => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/application/table/list/?application_id=${applicationId}&page_size=100`);
            if (resp.ok) {
                const data = await resp.json();
                setAppTables(Array.isArray(data) ? data : (data.results || []));
            }
        } catch (e) {
            console.error("Failed to load tables", e);
        }
    };

    const fetchTypes = async () => {
        setTypesLoading(true);
        try {
            let url = `/arcee/group-types/?application_id=${applicationId}`;
            const resp = await apiHelper.apiCall(url);
            if (resp.ok) {
                const typesData = await resp.json();
                const allTypes = Array.isArray(typesData) ? typesData : (typesData.results || []);

                // If parentGroup exists, we must only allow types that are children of parent's type
                if (parentGroup) {
                    // We need to fetch hierarchy rules to filter
                    const hResp = await apiHelper.apiCall(`/arcee/group-type-hierarchies/?application_id=${applicationId}`);
                    if (hResp.ok) {
                        const hData = await hResp.json();
                        const hierarchies = Array.isArray(hData) ? hData : (hData.results || []);

                        // Filter: types where parent_type matches parentGroup.group_type
                        // We need parentGroup's type ID.
                        // Assuming parentGroup object has group_type ID.
                        const pTypeId = (parentGroup.group_type && typeof parentGroup.group_type === 'object')
                            ? parentGroup.group_type.id
                            : parentGroup.group_type;

                        const allowedTypeIds = hierarchies
                            .filter(h => h.parent_type === pTypeId)
                            .map(h => h.child_type);

                        setAvailableTypes(allTypes.filter(t => allowedTypeIds.includes(t.id)));
                    }
                } else {
                    // Root group: allow only types that are NEVER a child in any hierarchy? 
                    // OR allow any type as root? 
                    // Let's filter types that are NOT child in any hierarchy rule? 

                    const hResp = await apiHelper.apiCall(`/arcee/group-type-hierarchies/?application_id=${applicationId}`);
                    if (hResp.ok) {
                        const hData = await hResp.json();
                        const hierarchies = Array.isArray(hData) ? hData : (hData.results || []);
                        const childTypeIds = new Set(hierarchies.map(h => h.child_type));
                        // Types that are never children
                        const rootTypes = allTypes.filter(t => !childTypeIds.has(t.id));
                        setAvailableTypes(rootTypes.length > 0 ? rootTypes : allTypes);
                    } else {
                        setAvailableTypes(allTypes);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setTypesLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !groupTypeId) return;

        setLoading(true);
        try {
            const payload = {
                application_id: applicationId,
                name: name.trim(),
                group_type: groupTypeId,
                parent: parentGroup ? parentGroup.id : null
            };

            let url = '/arcee/groups/create/';
            let method = 'POST';

            if (group) {
                // Update mode
                url = '/arcee/groups/update/';
                method = 'PATCH';
                payload.id = group.id;
                payload.parent = group.parent;
            }

            if (defaultTables && defaultTables.length > 0) {
                payload.default_tables = defaultTables;
            }

            const resp = await apiHelper.apiCall(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                const err = await resp.json();
                throw new Error(err.error || err.message || 'Failed to save group');
            }

            onSuccess();
            onClose();
        } catch (error) {
            showAlertDialog({
                title: 'Error',
                message: error.message,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content shadow-lg border-0" style={{ borderRadius: theme.cardRadius }}>
                    <div className="modal-header border-0 pb-0 pt-4 px-4 align-items-center">
                        <h5 className="modal-title fw-bold text-dark">
                            {group ? 'Edit Group' : (parentGroup ? `Add Sub-Group to "${parentGroup.name}"` : 'Create Root Group')}
                        </h5>
                        <button
                            type="button"
                            className="btn btn-light rounded-circle p-0 text-muted shadow-sm ms-auto"
                            onClick={onClose}
                            style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                backgroundColor: '#f8f9fa'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.classList.remove('text-muted');
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.color = '';
                                e.currentTarget.classList.add('text-muted');
                            }}
                        >
                            <i className="bi bi-x-lg" style={{ fontSize: '1rem' }}></i>
                        </button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body px-4 py-4">
                            <div className="mb-3">
                                <label className="form-label text-muted small fw-bold">GROUP NAME</label>
                                <input
                                    type="text"
                                    className="form-control shadow-sm"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Engineering, Alpha Team"
                                    required
                                    autoFocus
                                    style={{ borderRadius: theme.inputRadius, height: '3rem' }}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="form-label text-muted small fw-bold">GROUP TYPE</label>
                                <select
                                    className="form-select shadow-sm"
                                    value={groupTypeId}
                                    onChange={e => setGroupTypeId(e.target.value)}
                                    required
                                    disabled={typesLoading || availableTypes.length === 0}
                                    style={{ borderRadius: theme.inputRadius, height: '3rem' }}
                                >
                                    <option value="">{typesLoading ? 'Loading types...' : 'Select Type...'}</option>
                                    {availableTypes.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {!typesLoading && availableTypes.length === 0 && (
                                    <div className="form-text text-danger mt-2">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        No valid group types found for this level.
                                    </div>
                                )}
                            </div>

                            <div className="mb-2">
                                <label className="form-label text-muted small fw-bold mb-2">DEFAULT TABLE ACCESS</label>
                                <div className="input-group mb-2" style={{ borderRadius: theme.inputRadius, overflow: 'hidden' }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 shadow-none"
                                        placeholder="Search tables..."
                                        value={tableSearch}
                                        onChange={e => setTableSearch(e.target.value)}
                                        style={{ fontSize: '0.9rem' }}
                                    />
                                </div>
                                <div className="card border-0 shadow-sm bg-light">
                                    <div className="card-body p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {appTables.length === 0 && <small className="text-muted fst-italic p-2 d-block">No tables found.</small>}
                                        {appTables
                                            .filter(t =>
                                                (t.display_name || '').toLowerCase().includes(tableSearch.toLowerCase()) ||
                                                (t.name || '').toLowerCase().includes(tableSearch.toLowerCase())
                                            )
                                            .sort((a, b) => {
                                                const aSelected = defaultTables.includes(a.id);
                                                const bSelected = defaultTables.includes(b.id);
                                                if (aSelected === bSelected) return (a.display_name || a.name).localeCompare(b.display_name || b.name);
                                                return aSelected ? -1 : 1;
                                            })
                                            .map(t => (
                                                <div key={t.id} className={`form-check py-1 hover-bg-white rounded ps-4 pe-2 ${defaultTables.includes(t.id) ? 'bg-white shadow-sm mb-1' : ''}`}>
                                                    <input
                                                        className="form-check-input mt-1"
                                                        type="checkbox"
                                                        id={`tbl-${t.id}`}
                                                        checked={defaultTables.includes(t.id)}
                                                        onChange={() => {
                                                            if (defaultTables.includes(t.id)) {
                                                                setDefaultTables(defaultTables.filter(x => x !== t.id));
                                                            } else {
                                                                setDefaultTables([...defaultTables, t.id]);
                                                            }
                                                        }}
                                                    />
                                                    <label className="form-check-label small w-100" htmlFor={`tbl-${t.id}`} style={{ cursor: 'pointer' }}>
                                                        <span className={defaultTables.includes(t.id) ? 'fw-semibold text-primary' : ''}>
                                                            {t.display_name}
                                                        </span>
                                                        <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>({t.name})</span>
                                                    </label>
                                                </div>
                                            ))}
                                        {appTables
                                            .filter(t =>
                                                (t.display_name || '').toLowerCase().includes(tableSearch.toLowerCase()) ||
                                                (t.name || '').toLowerCase().includes(tableSearch.toLowerCase())
                                            ).length === 0 && appTables.length > 0 && (
                                                <small className="text-muted fst-italic p-2 d-block text-center mt-4">
                                                    No tables match your search.
                                                </small>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="form-text small mt-2 d-flex align-items-center">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Members will inherently access these tables.
                                </div>
                            </div>

                        </div>
                        <div className="modal-footer border-0 px-4 pb-4 pt-0">
                            <button
                                type="button"
                                className="btn btn-link text-decoration-none text-muted fw-bold"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary px-4 py-2 fw-bold shadow-sm"
                                disabled={loading || !name.trim() || !groupTypeId}
                                style={{
                                    background: theme.primary,
                                    border: 'none',
                                    borderRadius: theme.buttonRadius
                                }}
                            >
                                {loading ? 'Saving...' : 'Save Group'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
